// Horloge d'une scène pendant la lecture : PURE (le lecteur, les tests et la
// capture vidéo partagent ces règles).
//
//   - le temps de scène avance avec le temps réel quand on joue ;
//   - il s'ARRÊTE au début d'une porte (question, outil) non résolue ;
//   - avec une voix, il s'arrête au début de la phrase suivante tant que la
//     voix ne l'a pas commencée ; si la voix est en avance (phrase commencée
//     plus tôt que prévu), il accélère pour la rattraper : l'image suit la
//     voix dans les deux sens ;
//   - pendant la dernière phrase, il s'arrête à la fin estimée de la
//     narration tant que la voix n'a pas fini : ce qui suit la voix (une
//     question, la dernière étape) attend qu'elle ait vraiment fini ;
//   - la voix attend l'image : les débuts de phrase sont ÉTIRÉS par le
//     calendrier (temps de lecture des étapes), le lecteur ne laisse la voix
//     commencer une phrase que quand l'horloge y est (`voiceMayStart`) ;
//   - une scène est finie quand ses étapes (et leur temps de lecture) ET sa
//     narration sont finies et que toutes ses portes sont résolues ; on passe
//     à la suivante après un temps de lecture (`SCENE_END_HOLD_MS`).

import type { CompiledScene } from './timeline';
import type { ScheduledStep } from './schedule';

export interface ClockLimits {
  /** débuts des portes non résolues */
  gates: readonly number[];
  /** début de la prochaine phrase que la voix n'a pas commencée (`null` : pas d'attente) */
  nextSentenceStart: number | null;
  /** début de la phrase que dit la voix : l'horloge en retard accélère jusque-là */
  voiceSentenceStart?: number | null;
}

/** Facteur de rattrapage quand la voix est en avance sur l'image. */
export const CATCH_UP_RATE = 3;

/** Temps laissé pour regarder la fin d'une scène avant la suivante (ms). */
export const SCENE_END_HOLD_MS = 1200;

/** La scène finie depuis `completeAt` peut-elle laisser place à la suivante ? */
export function readyToAdvance(
  completeAt: number | null,
  t: number,
  holdMs = SCENE_END_HOLD_MS,
): boolean {
  return completeAt !== null && t - completeAt >= holdMs;
}

/** Temps de scène après `dtMs` de lecture. */
export function advanceSceneTime(t: number, dtMs: number, limits: ClockLimits): number {
  const behind = limits.voiceSentenceStart != null && t < limits.voiceSentenceStart;
  let next = t + Math.max(0, dtMs) * (behind ? CATCH_UP_RATE : 1);
  for (const g of limits.gates) if (g >= t) next = Math.min(next, g);
  const s = limits.nextSentenceStart;
  if (s !== null && s >= t) next = Math.min(next, s);
  return next;
}

/** Portes de la scène non résolues, par début. */
export function unresolvedGates(
  scene: Pick<CompiledScene, 'schedule'>,
  resolved: ReadonlySet<string>,
): ScheduledStep[] {
  return scene.schedule.steps
    .filter((s) => s.gate && !resolved.has(s.id))
    .sort((a, b) => a.startMs - b.startMs);
}

/**
 * Porte atteinte et non résolue (la scène attend l'élève), ou `null`.
 * `voiceAt` : où en est la voix (`voicePosition`) ; une porte ne s'ouvre pas
 * avant que la voix l'ait atteinte — une question posée après la narration
 * attend que la voix ait VRAIMENT fini, sans couper sa dernière phrase.
 */
export function activeGate(
  scene: Pick<CompiledScene, 'schedule'>,
  t: number,
  resolved: ReadonlySet<string>,
  voiceAt: number | null = null,
): ScheduledStep | null {
  const first = unresolvedGates(scene, resolved)[0];
  if (!first || first.startMs > t) return null;
  if (voiceAt !== null && voiceAt < first.startMs) return null;
  return first;
}

/**
 * Instant qu'attend l'horloge : la voix a commencé la phrase `voiceSentence`.
 * Le début de la phrase suivante ; pendant la dernière, la fin de la
 * narration (`narrationMs`) ; voix finie (`voiceSentence` = nombre de
 * phrases) : rien.
 */
export function nextSentenceStart(
  sentenceStartsMs: readonly number[],
  voiceSentence: number,
  narrationMs?: number,
): number | null {
  const next = sentenceStartsMs[voiceSentence + 1];
  if (next !== undefined) return next;
  if (narrationMs !== undefined && voiceSentence === sentenceStartsMs.length - 1)
    return narrationMs;
  return null;
}

/** Instant où en est la voix (rattrapage) : début de sa phrase, ou fin de la narration si elle a fini. */
export function voicePosition(
  sentenceStartsMs: readonly number[],
  voiceSentence: number,
  narrationMs: number,
): number | null {
  if (voiceSentence >= sentenceStartsMs.length) return narrationMs;
  return sentenceStartsMs[Math.max(0, voiceSentence)] ?? null;
}

/** Phrase affichée en sous-titre : celle de la voix, sinon celle de l'horloge. */
export function captionIndex(
  sentenceStartsMs: readonly number[],
  t: number,
  voiceSentence: number | null,
): number {
  if (voiceSentence !== null) return Math.max(0, voiceSentence);
  let k = 0;
  sentenceStartsMs.forEach((s, i) => {
    if (s <= t) k = i;
  });
  return k;
}

/**
 * La voix peut-elle commencer la phrase `index` ? Seulement quand l'horloge
 * de la scène a atteint son début (étiré) — elle attend que l'image ait fini
 * la phrase précédente et laissé le temps de la lire — et qu'aucune question
 * (porte) non résolue ne l'y précède : la suite n'est dite qu'après la
 * réponse. Une voix qui attend à ce bord a fini la phrase précédente : la
 * porte d'avant peut s'ouvrir (`activeGate` avec `voiceAt` = ce début).
 */
export function voiceMayStart(
  sentenceStartsMs: readonly number[],
  index: number,
  t: number,
  unresolvedGateStartsMs: readonly number[] = [],
): boolean {
  const s = sentenceStartsMs[index];
  if (s === undefined) return true;
  if (unresolvedGateStartsMs.some((g) => g <= s)) return false;
  return t >= s - 1;
}

export function isSceneComplete(
  scene: Pick<CompiledScene, 'schedule'>,
  t: number,
  narrationDone: boolean,
  resolved: ReadonlySet<string>,
): boolean {
  return (
    narrationDone && t >= scene.schedule.settledMs && unresolvedGates(scene, resolved).length === 0
  );
}
